import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Radio,
  notification,
} from 'antd';
import {
  Information,
  Drawer,
} from 'components/gyramais';
import GyramaisSlider from 'components/gyramais/Slider';
import { formatError, formatString } from 'utils';
import { useMutation, useQuery } from '@apollo/client';

import { LOAN, SAVE_LOAN, SAVE_LOAN_EVENT } from './graphql';

// Regras espelhadas do app do cliente (BadesulMicrocreditoPreApproved e
// BadesulMPEPreApproved): Microcrédito tem teto hard de R$ 5.000 em 12x;
// MPE parte de R$ 20.000 com escolha do tipo de taxa (TLP/FIXA/LCD).
const MICROCREDITO_MAX_AMOUNT = 5000;
const MICROCREDITO_MIN_AMOUNT = 1000;
const MICROCREDITO_INTEREST_RATE = '1,99% a.m.';
const MPE_MIN_AMOUNT = 20000;

const PreApprovedBadesulDrawer = ({
  loanId,
  visible,
  onClose,
  refetch,
}) => {
  const [chosenAmount, setChosenAmount] = useState(0);
  const [preApprovedOption, setPreApprovedOption] = useState();

  const { data: { loan } = {} } = useQuery(LOAN, {
    variables: { id: loanId },
    skip: !loanId,
  });

  const isMicrocredito = loan?.portfolioName === 'Badesul Microcrédito';
  const minAmount = isMicrocredito ? MICROCREDITO_MIN_AMOUNT : MPE_MIN_AMOUNT;
  const maxAmount = isMicrocredito
    ? Math.min(preApprovedOption?.finalAmount || 0, MICROCREDITO_MAX_AMOUNT)
    : preApprovedOption?.finalAmount || 0;
  // O valor pré-aprovado pode vir abaixo do piso do produto; nesse caso o
  // slider degeneraria (min > max) e não há valor válido a solicitar.
  const belowMinimum = !!preApprovedOption && maxAmount < minAmount;

  useEffect(() => {
    if (loan?.preApprovedOptions?.length) {
      setPreApprovedOption(loan.preApprovedOptions[0]);
    }
  }, [loan]);

  // Clampa dentro de [minAmount, maxAmount] mantendo o valor já ajustado pelo
  // parceiro — trocar o tipo de taxa (MPE) não deve descartar a escolha.
  useEffect(() => {
    if (!preApprovedOption) {
      return;
    }
    setChosenAmount((prev) => (
      prev ? Math.min(Math.max(prev, minAmount), maxAmount) : maxAmount
    ));
  }, [preApprovedOption]);

  const [saveLoan, { loading: saveLoading }] = useMutation(SAVE_LOAN, {
    onError: (error) => notification.error(formatError(error)),
  });

  const [saveLoanEvent, { loading: saveLoanLoading }] = useMutation(SAVE_LOAN_EVENT, {
    onCompleted: () => {
      refetch();
    },
    onError: (error) => notification.error(formatError(error)),
  });

  const loading = saveLoading || saveLoanLoading;

  const onSubmit = async () => {
    // Vencimento salvo é sempre dia 10 (mesmo comportamento do app; no MPE a
    // tela exibe dia 15, mas o backend trabalha com paymentDay 10)
    await saveLoan({
      variables: {
        id: loanId,
        paymentDay: 10,
      },
    });

    await saveLoanEvent({
      variables: {
        loanId,
        amount: chosenAmount,
        statusValue: 'pending',
        installmentsCount: preApprovedOption.finalInstallmentsCount,
        taxRateType: preApprovedOption.taxRateType
          || loan?.additionalInfo?.taxRateType,
      },
    });

    onClose();
  };

  const handleClose = () => {
    if (loading) {
      return;
    }

    onClose();
  };

  return (
    <Drawer
      open={visible}
      onClose={handleClose}
      className="pre-approved-drawer"
      width="70%"
      primaryButton={{
        disabled: !preApprovedOption || belowMinimum || chosenAmount < minAmount,
        onClick: () => {
          onSubmit();
        },
      }}
    >
      <Row>
        <Information
          loading={loading}
          label="Total pré-aprovado"
          value={formatString(maxAmount, 'currency')}
        />
      </Row>
      <Row>
        <Information
          loading={loading}
          label="Taxa"
          value={isMicrocredito ? (
            <div>{MICROCREDITO_INTEREST_RATE}</div>
          ) : (
            <Radio.Group
              onChange={(e) => setPreApprovedOption(e.target.value)}
              value={preApprovedOption}
            >
              {loan?.preApprovedOptions?.map((option) => (
                <Radio key={option.taxRateType} value={option}>
                  {option.taxRateType}
                </Radio>
              ))}
            </Radio.Group>
          )}
        />
      </Row>
      {!isMicrocredito && (
        <Row>
          <Information
            loading={loading}
            label="Carência"
            value="6 meses"
          />
        </Row>
      )}
      <Row>
        <Information
          loading={loading}
          label="Parcelas"
          value={isMicrocredito
            ? '12 parcelas mensais de amortização'
            : '2 trimestrais de carência e 54 mensais de amortização'}
        />
      </Row>
      <Row>
        <Information
          loading={loading}
          label="Vencimento"
          value={isMicrocredito ? 'Todo dia 10' : 'Todo dia 15'}
        />
      </Row>

      <Row gutter={[0, 30]}>
        <Col span={24}>
          <h6 className="no-margin-bottom">
            <b className="secondary-text">
              Valor solicitado
            </b>
          </h6>
        </Col>

        <Col span={24}>
          {belowMinimum ? (
            <p className="secondary-text">
              O valor pré-aprovado (
              {formatString(maxAmount, 'currency')}
              ) está abaixo do mínimo do produto (
              {formatString(minAmount, 'currency')}
              ). Não é possível prosseguir — acione o time Gyra+.
            </p>
          ) : (
            <GyramaisSlider
              tooltipVisible={false}
              min={minAmount}
              max={maxAmount}
              value={chosenAmount}
              onChange={(e) => setChosenAmount(e)}
              step={500}
              showValues
              valuesFormatter={(e) => formatString(e, 'currency')}
              input={{ type: 'currency' }}
              loading={loading}
            />
          )}
        </Col>
      </Row>

      {!isMicrocredito && (
        <Row>
          <p className="secondary-text">
            Financiamento possui taxa de análise (0,8% do valor contratado) que
            deve ser paga antes da assinatura da CCB (Cédula de Crédito
            Bancário).
            <br />
            Financiamento com incidência de IOF e desconto da taxa de
            contratação do fundo garantidor FGI PEAC.
          </p>
        </Row>
      )}
    </Drawer>
  );
};

PreApprovedBadesulDrawer.propTypes = {
  loanId: PropTypes.string,
  visible: PropTypes.bool,
  onClose: PropTypes.func,
  refetch: PropTypes.func,
};

export default PreApprovedBadesulDrawer;
